/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React from "react";
import { useTheme } from "../../theme/ThemeContext";
import ThemedToggle from "../common/ThemedToggle";
import UniqueConstraintBuilder from "./UniqueConstraintBuilder";
import RowLimitBuilder from "./RowLimitBuilder";
import ScheduledTransitionBuilder from "./ScheduledTransitionBuilder";
import CheckConstraintBuilder from "./CheckConstraintBuilder";
import CompositeIndexBuilder from "./CompositeIndexBuilder";
import ReadFilterBuilder from "./ReadFilterBuilder";
import {
  isDisplayNameValid,
  isDescriptionValid,
} from "../../utils/validations";

const CREATION_TIME_TOOLTIP = "This setting cannot be changed after the table is created.";

/**
 * Props:
 *   table            — full table state object
 *   onChange         — (key, value) => void
 *   errors           — { display_name?, description?, ... }
 *   isEditMode       — boolean (always true from EditTableForm)
 *   availableColumns — array of { name, data_type } for constraint/index builders
 *   tableDbId        — number|null (used for public pages banner)
 *   onNavigatePublic — () => void
 *   showMetadata     — boolean (default true). When false, only the Schema
 *                      Settings section is rendered (name/visibility/etc. are
 *                      omitted) — used by the create form which owns those fields.
 */
export default function TableSettingsPanel({
  table,
  onChange,
  errors = {},
  isEditMode,
  availableColumns = [],
  allTables = [],
  tableDbId,
  onNavigatePublic,
  showMetadata = true,
  hasRows = false,
  onEditingChange,
}) {
  const { theme } = useTheme();

  const editFlags = React.useRef({ unique: false, check: false, index: false });
  const reportEditing = (key) => (open) => {
    editFlags.current[key] = open;
    const any = editFlags.current.unique || editFlags.current.check || editFlags.current.index;
    onEditingChange && onEditingChange(any);
  };

  const labelStyle = { color: theme.primary, fontWeight: 600 };
  const hintStyle  = { fontSize: 13, color: "#636979", marginTop: 2 };
  const errorStyle = { color: "red", fontSize: 12, marginTop: 4 };

  const sectionHeadStyle = {
    color: theme.primary,
    fontWeight: 700,
    fontSize: 15,
    marginTop: 28,
    marginBottom: 6,
    borderBottom: `1px solid ${theme.borderColor || "#e2e8f0"}`,
    paddingBottom: 4,
  };

  return (
    <>
      {showMetadata && (
      <>
      {/* ── Table Name (always read-only) ── */}
      <div className="mb-4">
        <label style={labelStyle}>Table Name</label>
        <input className="form-control" value={table.name} disabled />
      </div>

      {/* ── Display Name ── */}
      <div className="mb-4">
        <label style={labelStyle}>Display Name*</label>
        <input
          className="form-control"
          value={table.display_name}
          onChange={e => onChange("display_name", e.target.value)}
        />
        {errors.display_name && <div style={errorStyle}>{errors.display_name}</div>}
        <div style={hintStyle}>
          <i className="bi bi-info-circle" /> <b>Required.</b> Alphabets, numbers, and spaces only.
        </div>
      </div>

      {/* ── Description ── */}
      <div className="mb-4">
        <label style={labelStyle}>Description</label>
        <textarea
          className="form-control"
          value={table.description}
          onChange={e => onChange("description", e.target.value)}
          rows={2}
        />
        {errors.description && <div style={errorStyle}>{errors.description}</div>}
        <div style={hintStyle}>
          <i className="bi bi-info-circle" /> Alphabets, numbers, and spaces only. {(table.description || "").length}/500 characters.
        </div>
      </div>

      {/* ── Visibility ── */}
      <div className="mb-4">
        <label style={labelStyle}>Visibility</label>
        <select
          className="form-control"
          value={table.visibility}
          onChange={e => onChange("visibility", e.target.value)}
          style={{ borderRadius: theme.buttonRadius }}
        >
          <option value="public">Public</option>
          <option value="private">Private</option>
          <option value="hidden">Hidden</option>
        </select>
        <div style={hintStyle}>
          <i className="bi bi-info-circle" /> Control access: Public (Everyone), Private (Authorized), Hidden (Admin only).
        </div>
      </div>

      {/* ── Public Pages banner ── */}
      {table.visibility === "public" && tableDbId && (
        <div className="mb-4 p-3 rounded d-flex align-items-center gap-3"
          style={{ background: "#ecfeff", border: "1px solid #22d3ee" }}>
          <i className="bi bi-globe2" style={{ fontSize: 20, color: "#0891b2" }} />
          <div className="flex-grow-1">
            <div className="fw-semibold small" style={{ color: "#0e7490" }}>This table is public.</div>
            <div className="text-muted" style={{ fontSize: 12 }}>Configure how visitors see this table at /p/.</div>
          </div>
          <button type="button" className="btn btn-sm"
            style={{ background: "#0891b2", color: "#fff", border: "none", whiteSpace: "nowrap" }}
            onClick={onNavigatePublic}>
            <i className="bi bi-sliders me-1" /> Open Public View Builder
          </button>
        </div>
      )}

      </>
      )}

      {/* ── A1 Schema Settings ── */}
      <div style={sectionHeadStyle}>Schema Settings</div>

      {/* soft_delete */}
      <div className="mb-4">
        <label style={labelStyle}>
          Soft Delete
          {isEditMode && (
            <i className="bi bi-lock-fill ms-2" style={{ fontSize: 13, color: "#888" }}
              title={CREATION_TIME_TOOLTIP} />
          )}
        </label>
        {isEditMode ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
            <span className="badge" style={{ background: table.soft_delete ? theme.primary : "#94a3b8", color: "#fff", padding: "4px 12px", borderRadius: 12 }}>
              {table.soft_delete ? "Enabled" : "Disabled"}
            </span>
            <span style={{ fontSize: 12, color: "#888" }} title={CREATION_TIME_TOOLTIP}>
              <i className="bi bi-info-circle" /> Cannot change after creation
            </span>
          </div>
        ) : (
          <ThemedToggle
            checked={table.soft_delete}
            onChange={val => onChange("soft_delete", val)}
            onLabel="Enabled"
            offLabel="Disabled"
          />
        )}
        <div style={hintStyle}>
          <i className="bi bi-info-circle" /> When enabled, DELETE requests mark records with a timestamp instead of removing them. Deleted records are automatically excluded from reads.
        </div>
      </div>

      {/* pk_strategy */}
      <div className="mb-4">
        <label style={labelStyle}>
          Primary Key Strategy
          {isEditMode && (
            <i className="bi bi-lock-fill ms-2" style={{ fontSize: 13, color: "#888" }}
              title={CREATION_TIME_TOOLTIP} />
          )}
        </label>
        {isEditMode ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
            <span className="badge" style={{ background: theme.secondary, color: "#fff", padding: "4px 12px", borderRadius: 12 }}>
              {table.pk_strategy === "uuid" ? "UUID" : "Serial (Auto-increment)"}
            </span>
            <span style={{ fontSize: 12, color: "#888" }} title={CREATION_TIME_TOOLTIP}>
              <i className="bi bi-info-circle" /> Cannot change after creation
            </span>
          </div>
        ) : (
          <select className="form-select" value={table.pk_strategy || "serial"}
            onChange={e => onChange("pk_strategy", e.target.value)}>
            <option value="serial">Serial (Auto-increment integer)</option>
            <option value="uuid">UUID (Auto-generated UUID v4)</option>
          </select>
        )}
        <div style={hintStyle}>
          <i className="bi bi-info-circle" /> Determines the type of primary key generated for each record.
        </div>
      </div>

      {/* syncable */}
      <div className="mb-4">
        <label style={labelStyle}>
          Syncable (Mobile Offline Sync)
          {isEditMode && (
            <i className="bi bi-lock-fill ms-2" style={{ fontSize: 13, color: "#888" }}
              title={CREATION_TIME_TOOLTIP} />
          )}
        </label>
        {isEditMode ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
            <span className="badge" style={{ background: table.syncable ? theme.primary : "#94a3b8", color: "#fff", padding: "4px 12px", borderRadius: 12 }}>
              {table.syncable ? "Enabled" : "Disabled"}
            </span>
            <span style={{ fontSize: 12, color: "#888" }} title={CREATION_TIME_TOOLTIP}>
              <i className="bi bi-info-circle" /> Cannot change after creation
            </span>
          </div>
        ) : (
          <ThemedToggle
            checked={table.syncable || false}
            onChange={val => onChange("syncable", val)}
            onLabel="Enabled"
            offLabel="Disabled"
          />
        )}
        <div style={hintStyle}>
          <i className="bi bi-info-circle" /> When enabled, this table participates in the
          mobile↔server sync mechanism (idempotent create, batch push, delta pull). Requires
          Primary Key Strategy = UUID and Soft Delete = Enabled. Cannot be changed after creation.
        </div>
      </div>

      {/* is_global — unlike soft_delete/syncable this one is editable after
          creation: it changes no schema, only how rows are filtered on read. */}
      <div className="mb-4">
        <label style={labelStyle}>Shared Reference Table</label>
        <ThemedToggle
          checked={table.is_global || false}
          onChange={val => onChange("is_global", val)}
          onLabel="Enabled"
          offLabel="Disabled"
        />
        <div style={hintStyle}>
          <i className="bi bi-info-circle" /> Turn this on for lookup tables whose rows belong to
          nobody — activity types, units, categories. Row-level security is skipped when reading,
          so every user who may open the table sees all of its rows instead of an empty list.
          Reads only: saving, editing and deleting stay scoped as usual, and role permissions
          still decide who can reach the table. Leave it off for anything holding user records.
        </div>
      </div>

      {/* ── Unique Constraints ── */}
      <div className="mb-4">
        <label style={labelStyle}>Unique Constraints</label>
        <div style={hintStyle}><i className="bi bi-info-circle" /> Enforce that a combination of field values is unique across all records.</div>
        <div style={{ marginTop: 10 }}>
          <UniqueConstraintBuilder
            value={table.unique_constraints || []}
            onChange={val => onChange("unique_constraints", val)}
            availableColumns={availableColumns}
            hasRows={hasRows}
            onEditingChange={reportEditing("unique")}
          />
        </div>
      </div>

      {/* ── Row Limits ── */}
      <div className="mb-4">
        <label style={labelStyle}>Row Limits (DB Locking)</label>
        <div style={hintStyle}><i className="bi bi-info-circle" /> Cap how many records may exist per group — places on an event, seats in a class, slots in a day. Held correctly even when many people submit at the same moment.</div>
        <div style={{ marginTop: 10 }}>
          <RowLimitBuilder
            value={table.row_limits || []}
            onChange={val => onChange("row_limits", val)}
            availableColumns={availableColumns}
            allTables={allTables}
            onEditingChange={reportEditing("rowlimit")}
          />
        </div>
      </div>

      {/* ── Scheduled Changes ── */}
      <div className="mb-4">
        <label style={labelStyle}>Scheduled Changes</label>
        <div style={hintStyle}><i className="bi bi-info-circle" /> Change a value when a time passes, with nobody there to do it — registration opening on Monday, an event marking itself finished once it is over.</div>
        <div style={{ marginTop: 10 }}>
          <ScheduledTransitionBuilder
            value={table.scheduled_transitions || []}
            onChange={val => onChange("scheduled_transitions", val)}
            availableColumns={availableColumns}
            allTables={allTables}
            onEditingChange={reportEditing("schedule")}
          />
        </div>
      </div>

      {/* ── Check Constraints ── */}
      <div className="mb-4">
        <label style={labelStyle}>Check Constraints</label>
        <div style={hintStyle}><i className="bi bi-info-circle" /> Define rules that field values must satisfy (e.g. age &gt; 0).</div>
        <div style={{ marginTop: 10 }}>
          <CheckConstraintBuilder
            value={table.check_constraints || []}
            onChange={val => onChange("check_constraints", val)}
            availableColumns={availableColumns}
            hasRows={hasRows}
            onEditingChange={reportEditing("check")}
          />
        </div>
      </div>

      {/* ── Composite Indexes ── */}
      <div className="mb-4">
        <label style={labelStyle}>Composite Indexes</label>
        <div style={hintStyle}><i className="bi bi-info-circle" /> Indexes spanning multiple fields to speed up queries.</div>
        <div style={{ marginTop: 10 }}>
          <CompositeIndexBuilder
            value={table.composite_indexes || []}
            onChange={val => onChange("composite_indexes", val)}
            availableColumns={availableColumns}
            onEditingChange={reportEditing("index")}
          />
        </div>
      </div>

      {/* ── Row Filter (no-code) ── */}
      <div className="mb-4">
        <label style={labelStyle}>Row Filter</label>
        <div style={hintStyle}>
          <i className="bi bi-info-circle" /> Limit which rows this table returns on reads.
          Users with edit access always see all rows.
        </div>
        {table.visibility === "public" && (
          <div className="p-2 mb-2 rounded" style={{ background: "#ecfeff", border: "1px solid #22d3ee", fontSize: 13, color: "#0e7490", marginTop: 8 }}>
            <i className="bi bi-globe2 me-1" /> This table is public — set a filter to control which rows visitors can see.
          </div>
        )}
        <div style={{ marginTop: 10 }}>
          <ReadFilterBuilder
            value={table.read_filter || null}
            onChange={val => onChange("read_filter", val)}
            availableColumns={availableColumns}
          />
        </div>
      </div>
    </>
  );
}
